'use client';

import {forwardRef, useEffect, useLayoutEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

export const VideoAlphaChannel = forwardRef(
  (
    {
      srcVp9,
      srcH265,
      className = '',
      options: {
        autoPlay = true,
        loop = true,
        muted = true,
        playsInline = true,
        controls = false,
      } = {},
      inViewAutoPlay = false,
      style = null,
      onClick = () => {},
      onCanPlay = () => {},
      onEnded = () => {},
      'data-qa-id': dataQaId = null,
      id = null,
      width = null,
      height = null,
      portalToBody = false,
    },
    videoRef
  ) => {
    const [loaded, setLoaded] = useState(false);
    const [inView, setInView] = useState(false);
    // eslint-disable-next-line no-param-reassign
    if (!videoRef) videoRef = useRef(null);

    useEffect(() => {
      if (videoRef?.current && loaded) {
        videoRef.current.load();
      } else if (!loaded) {
        setLoaded(true);
      }
    }, [srcVp9, srcH265]);

    useEffect(() => {
      if (videoRef?.current) {
        // eslint-disable-next-line no-param-reassign
        videoRef.current.defaultMuted = muted;
      }
    }, []);

    useLayoutEffect(() => {
      // TODO: This is sufficient for the mobile create flow, but need to be able to control the root
      // element for more use cases, like desktop create.
      if (!inViewAutoPlay || !videoRef?.current) return undefined;
      const element = videoRef.current;
      const callback = (o) => {
        if (o[0].isIntersecting) {
          setInView(true);
          videoRef.current.play();
        }
      };
      const observer = new IntersectionObserver(callback, {
        root: null,
        threshold: 0,
        rootMargin: '50px',
      });
      observer.observe(element);
      return () => observer.unobserve(element);
    }, [inViewAutoPlay]);

    const videoComponent = (
      /* eslint-disable-next-line jsx-a11y/media-has-caption */
      <video
        preload="auto"
        ref={videoRef}
        className={className}
        autoPlay={inViewAutoPlay ? inView : autoPlay}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        controls={controls}
        onClick={onClick}
        onCanPlay={onCanPlay}
        style={style}
        data-qa-id={dataQaId}
        id={id}
        width={width}
        height={height}
        onEnded={onEnded}
        poster="/assets/images/transparent-pixel.png"
      >
        <source src={srcH265} type="video/mp4;codecs=hvc1" />
        <source src={srcVp9} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    );

    return portalToBody ? ReactDOM.createPortal(videoComponent, document.body) : videoComponent;
  }
);

VideoAlphaChannel.propTypes = {
  srcVp9: PropTypes.string.isRequired,
  srcH265: PropTypes.string.isRequired,
  className: PropTypes.string,
  options: PropTypes.oneOfType([
    PropTypes.shape({
      autoPlay: PropTypes.bool,
      loop: PropTypes.bool,
      muted: PropTypes.bool,
      playsInline: PropTypes.bool,
    }),
    PropTypes.oneOf([null]),
  ]),
  inViewAutoPlay: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  onClick: PropTypes.func,
  onCanPlay: PropTypes.func,
  onEnded: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  portalToBody: PropTypes.bool,
};
